import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useLoginWithCode, useOnboardingState } from "@/lib/api";
import { logger } from "@/lib/logger";

import { CodeEntry } from "./code-entry";
import { FlowShell } from "./flow-shell";
import { LoadingScreen } from "./loading-screen";

/** Digits in a kit access code, as printed on the household's flyer. */
const CODE_LENGTH = 6;

export function OnboardingPage() {
  const [params, setParams] = useSearchParams();
  const { data: state, isLoading } = useOnboardingState();
  const login = useLoginWithCode();

  // The kit flyer is generated per household, so its QR can carry the access
  // code: /onboarding?code=NNNNNN drops the household straight into their own
  // flow. Scanning the box is then the whole sign-in — no six digits to
  // re-type, and no "are you staff?" fork in between.
  //
  // Everything below keys off `hasCodeParam` — *a code arrived* — rather than
  // `hasLinkCode` — *and it was well-formed*. A flyer is printed, so a code can
  // reach us truncated or mistyped, and a malformed one still has to be taken
  // out of the URL and still has to be explained.
  const codeParam = params.get("code");
  const hasCodeParam = codeParam !== null;
  const linkCode = (codeParam ?? "").replace(/\D/g, "");
  const hasLinkCode = linkCode.length === CODE_LENGTH;
  const exchanged = useRef(false);
  const [linkFailed, setLinkFailed] = useState(false);

  // A one-shot imperative exchange, not data loading — the "no useEffect for
  // data loading" rule is about queries, and this is a credential swap that
  // has to happen exactly once per arrival. The ref guard is what enforces
  // "once": both StrictMode's double-invoke and any re-render would otherwise
  // fire a second login.
  useEffect(() => {
    // Wait for the session check to settle before touching the code: whether
    // we should exchange at all depends on whether this browser already has a
    // session.
    if (!hasCodeParam || exchanged.current || isLoading) {
      return;
    }
    exchanged.current = true;

    // Drop the code from the URL before doing anything with it. It is a bearer
    // credential: left in place it lands in browser history, in any screenshot
    // the household takes of their setup, and in the URL they'd paste to ask
    // someone for help. `replace` so Back can't navigate onto it either.
    const next = new URLSearchParams(params);
    next.delete("code");
    setParams(next, { replace: true });

    // Already signed in? Ignore the code and resume where they left off.
    //
    // This is not just an optimisation. A REJECTED code exchange clears the
    // session cookie server-side, so exchanging unconditionally means a
    // household who re-scans a stale QR, an old flyer, or a flyer belonging to
    // another household is logged out mid-install and dumped on the code form
    // — after they've already finished several compartments. Re-scanning your
    // own box should be a no-op, and this makes it one.
    if (state) {
      logger.debug("[onboarding] already signed in — ignoring flyer link code");
      return;
    }

    // A code that isn't six digits can't be exchanged — a truncated QR, a
    // hand-copied link, a stray character. Say so, rather than dropping them on
    // a blank form that looks like scanning the box did nothing.
    if (!hasLinkCode) {
      logger.warn("[onboarding] flyer link code is malformed — falling back to manual entry");
      setLinkFailed(true);
      return;
    }

    logger.debug("[onboarding] exchanging access code from flyer link");
    void login.mutateAsync({ code: linkCode }).catch((err) => {
      // Never dead-end on a bad link — fall through to manual entry, which is
      // still the path for a household on a second device or without the
      // flyer to hand.
      logger.warn("[onboarding] flyer link code rejected:", err);
      setLinkFailed(true);
    });
  }, [hasCodeParam, hasLinkCode, isLoading, linkCode, login, params, setParams, state]);

  // Data wins over error: TanStack Query keeps previous data when a
  // background refetch fails, and a mid-flow user on a flaky network (they
  // are literally unplugging network gear during setup) must not be bounced
  // back to code entry by a transient blip.
  if (state) {
    return <FlowShell state={state} />;
  }

  // Hold the loading screen while a flyer code is in flight. `hasCodeParam`
  // covers the first render (the effect hasn't run yet) and `isPending` covers
  // the exchange itself; without both, the code form flashes up and vanishes,
  // which reads as a glitch on the very first screen of the product. A
  // malformed code resolves inside the effect, which strips the param and sets
  // `linkFailed` in one batch — so that path lands on the form already carrying
  // its explanation, never on a bare one.
  if (isLoading || login.isPending || (hasCodeParam && !linkFailed)) {
    return <LoadingScreen />;
  }

  // No data and not loading — any failure (401 no session, 404 no household,
  // network error, 5xx) lands here: the visitor enters their access code.
  return (
    <CodeEntry
      initialError={
        linkFailed ? "That link didn't work. Enter the code from your kit flyer instead." : ""
      }
    />
  );
}
